import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { activelogin, coderefresh } from '../../services/auth';
import { useAuth } from '../../contexts/AuthContext';
import { encrypt, decrypt } from '../../utils/crypto';
import { consumeReturnTo, returnStateFrom } from '../../utils/authRedirect';
import { trackSignUp } from '../../analytics/conversions';

const ActivationCode = () => {
  const [activationCode, setActivationCode] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const checkStoredTimer = () => {
      try {
        const encryptedTimer = localStorage.getItem('activationTimer');
        const encryptedExpiry = localStorage.getItem('activationTimerExpiry');
        
        if (encryptedTimer && encryptedExpiry) {
          const storedTimer = decrypt(encryptedTimer);
          const expiryTime = decrypt(encryptedExpiry);
          const now = new Date().getTime();
          
          if (storedTimer && expiryTime) {
            const remainingTime = Math.floor((expiryTime - now) / 1000);
            if (remainingTime > 0) {
              setTimer(remainingTime);
            } else {
              localStorage.removeItem('activationTimer');
              localStorage.removeItem('activationTimerExpiry');
            }
          }
        } else {
          setTimer(120);
          const expiryTime = new Date().getTime() + (120 * 1000);
          localStorage.setItem('activationTimer', encrypt(120));
          localStorage.setItem('activationTimerExpiry', encrypt(expiryTime));
        }
      } catch (error) {
        console.error('Timer check error:', error);
      }
    };

    checkStoredTimer();
  }, []);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          const newTimer = prevTimer - 1;
          try {
            if (newTimer === 0) {
              localStorage.removeItem('activationTimer');
              localStorage.removeItem('activationTimerExpiry');
            } else {
              const expiryTime = new Date().getTime() + (newTimer * 1000);
              localStorage.setItem('activationTimer', encrypt(newTimer));
              localStorage.setItem('activationTimerExpiry', encrypt(expiryTime));
            }
          } catch (error) {
            console.error('Timer update error:', error);
          }
          return newTimer;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timer]);

  const handleActivation = async (e) => {
    e.preventDefault();
    
    try {
      const response = await activelogin(activationCode);

      if (response.statusCode === 200) {
        const userData = {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          isActivated: true
        };
        setUser(userData);
        toast.success(t('activation.success'));
        
        localStorage.removeItem('activationTimer');
        localStorage.removeItem('activationTimerExpiry');
        
        // Dönüş adresi (state ya da aynı sekmedeki yedek) varsa oraya; yoksa önceki varsayılan hedef
        const returnTo = consumeReturnTo(location);
        trackSignUp(returnTo);
        navigate(returnTo || '/advertisements');
      } else {
        setAttempts(prev => prev + 1);
        if (attempts >= 2) {
          localStorage.removeItem('activationTimer');
          localStorage.removeItem('activationTimerExpiry');
          
          toast.error(t('activation.tooManyAttempts'));
          navigate('/login', { state: returnStateFrom(location) });
          return;
        }
        toast.error(response.message);
      }
    } catch (error) {
      setAttempts(prev => prev + 1);
      if (attempts >= 2) {
        localStorage.removeItem('activationTimer');
        localStorage.removeItem('activationTimerExpiry');
        
        toast.error(t('activation.tooManyAttempts'));
        navigate('/login', { state: returnStateFrom(location) });
        return;
      }
      toast.error(t('activation.error'));
    }
  };

  const handleResendCode = async () => {
    if (timer > 0) return;
    
    setIsResending(true);
    try {
      const response = await coderefresh();
      if (response.statusCode === 200) {
        const newTimer = 120;
        const expiryTime = new Date().getTime() + (newTimer * 1000);
        localStorage.setItem('activationTimer', encrypt(newTimer));
        localStorage.setItem('activationTimerExpiry', encrypt(expiryTime));
        setTimer(newTimer);
        toast.success(t('activation.codeResent'));
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('activation.resendError'));
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const timerPercentage = (timer / 120) * 100;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: 8,
        px: 2,
        background: theme.palette.background.default,
        transition: 'background-color 0.3s ease'
      }}
    >
      <Container maxWidth="sm">
        <Paper 
          elevation={theme.palette.mode === 'dark' ? 3 : 1}
          sx={{ 
            p: { xs: 3, sm: 4 }, 
            borderRadius: 2,
            background: theme.palette.background.paper,
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ 
              color: theme.palette.text.primary,
              fontSize: isMobile ? '1.75rem' : '2.125rem'
            }}
          >
            {t('activation.title')}
          </Typography>

          <Typography 
            variant="subtitle1" 
            align="center" 
            sx={{ 
              color: theme.palette.text.secondary,
              mb: 4
            }}
          >
            {t('activation.instruction')}
          </Typography>

          {timer > 0 && (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                mb: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: theme.palette.action.hover
              }}
            >
              <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                <CircularProgress
                  variant="determinate"
                  value={timerPercentage}
                  size={60}
                  thickness={4}
                  sx={{
                    color: theme.palette.primary.main
                  }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography 
                    variant="h6" 
                    component="div" 
                    color="text.primary"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {formatTime(timer)}
                  </Typography>
                </Box>
              </Box>
              <Typography 
                variant="body2" 
                color="text.secondary"
                align="center"
              >
                {t('activation.nextCodeIn')}
              </Typography>
            </Box>
          )}

          <Box component="form" noValidate onSubmit={handleActivation}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="activationCode"
              label={t('activation.codeLabel')}
              type="text"
              id="activationCode"
              value={activationCode}
              onChange={(e) => setActivationCode(e.target.value)}
              inputProps={{
                maxLength: 6,
                style: { 
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  letterSpacing: '0.5rem'
                }
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              }}
            >
              {t('activation.buttonText')}
            </Button>

            <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResendCode}
                disabled={isResending || timer > 0}
                sx={{ 
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                {isResending ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    {t('activation.resending')}
                  </Box>
                ) : timer > 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    <Box sx={{ position: 'relative', display: 'inline-flex', mr: 2 }}>
                      <CircularProgress
                        variant="determinate"
                        value={timerPercentage}
                        size={24}
                        thickness={4}
                      />
                      <Box
                        sx={{
                          top: 0,
                          left: 0,
                          bottom: 0,
                          right: 0,
                          position: 'absolute',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="caption" component="div" color="text.secondary">
                          {Math.round(timerPercentage)}%
                        </Typography>
                      </Box>
                    </Box>
                    {`${t('activation.waitBeforeResend')} (${formatTime(timer)})`}
                  </Box>
                ) : (
                  t('activation.resendButtonText')
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ActivationCode; 